import type { components } from "@oathera/contract/api";
import type { StatusLevel } from "../../components/index.ts";
import type {
  ComplianceFramework,
  ComplianceMappingResponse,
  ComplianceReference,
  MappedFinding,
} from "./use-compliance.ts";

/**
 * Presentation vocabulary for the Compliance screen (task 10.3; design C6, 12.4). It keeps the
 * one-way maps from the generated contract shapes onto the screen's presentational pieces in a
 * single, testable place, mirroring the Posture screen's `vocab.ts`. It defines NO API shapes
 * (those come from `@oathera/contract/api` via `use-compliance.ts`).
 *
 * IMPORTANTLY it holds NO client-side catalog of framework references (Requirement 4.5): the four
 * frameworks are the generated {@link ComplianceFramework} enum, so this module maps only the FOUR
 * enum values onto human display labels — the reference ids/titles and their findings come solely
 * from the server response. There is no per-reference metadata, no reference list, and no finding
 * fabricated here.
 *
 * SEVERITY / RISK TIER / CONFIDENCE are conveyed by icon+text (a `StatusBadge`), never colour alone
 * (design 12.4). A mapped finding carries EITHER a `severity` (assurance findings) OR a `risk_tier`
 * (posture findings); both are nullable per contract, and a null is shown honestly rather than
 * invented. The crosswalk `confidence` (`direct | related | informational`) is the strength of the
 * mapping itself, not of the finding — it gets its own label + rank so the drill-down can order its
 * findings by how firmly their signal crosswalks to the reference.
 */

type FindingSeverity = components["schemas"]["enum_finding_severity"];
type RiskTier = components["schemas"]["enum_risk_tier"];
type Confidence = MappedFinding["confidence"];
type SignalKind = MappedFinding["signal"]["kind"];

/* ---- Framework labels (enum → display, NOT a reference catalog) ----------------------------- */

/**
 * The four frameworks in a stable presentation order, straight off the generated
 * {@link ComplianceFramework} enum. This is the only place the SPA enumerates the frameworks; it is
 * the enum's own members, not a hand-authored catalog, so adding a framework to the contract is an
 * additive change the picker picks up once this list mirrors the enum (Requirement 6.3). The
 * reference lists for each framework still come only from the server response.
 */
export const COMPLIANCE_FRAMEWORKS: readonly ComplianceFramework[] = [
  "mitre_atlas",
  "owasp_llm_top10",
  "nist_ai_rmf",
  "nist_800_53",
];

/** Short human label for a framework (used on the picker control). */
export function frameworkLabel(framework: ComplianceFramework): string {
  switch (framework) {
    case "mitre_atlas":
      return "MITRE ATLAS";
    case "owasp_llm_top10":
      return "OWASP LLM Top 10";
    case "nist_ai_rmf":
      return "NIST AI RMF";
    case "nist_800_53":
      return "NIST SP 800-53";
  }
}

/** A longer accessible phrase for a framework, used on the picker's tab list and labels. */
export function frameworkTitle(framework: ComplianceFramework): string {
  switch (framework) {
    case "mitre_atlas":
      return "MITRE ATLAS — adversarial-ML technique families and techniques";
    case "owasp_llm_top10":
      return "OWASP Top 10 for LLM Applications";
    case "nist_ai_rmf":
      return "NIST AI Risk Management Framework 1.0";
    case "nist_800_53":
      return "NIST SP 800-53 Rev. 5 security and privacy controls";
  }
}

/* ---- Finding severity (assurance findings) -------------------------------------------------- */

/**
 * Map a finding severity onto a status level for the icon+text `StatusBadge` (design 12.4).
 * critical/high are `error`, medium is `warn`, low/info are `neutral` (a real-but-low severity, not
 * a reassuring green), and the catalog `other` is grey `unknown`. No severity maps to `healthy`: a
 * finding is never shown as a reassuring green.
 */
export function severityStatus(severity: FindingSeverity): StatusLevel {
  switch (severity) {
    case "critical":
    case "high":
      return "error";
    case "medium":
      return "warn";
    case "low":
    case "info":
      return "neutral";
    case "other":
      return "unknown";
  }
}

/** Human label for a finding severity. */
export function severityLabel(severity: FindingSeverity): string {
  switch (severity) {
    case "critical":
      return "Critical";
    case "high":
      return "High";
    case "medium":
      return "Medium";
    case "low":
      return "Low";
    case "info":
      return "Info";
    case "other":
      return "Other";
  }
}

/** Severity rank (higher is more severe); `other` ranks lowest so it never jumps ahead. */
export function severityRank(severity: FindingSeverity): number {
  switch (severity) {
    case "critical":
      return 5;
    case "high":
      return 4;
    case "medium":
      return 3;
    case "low":
      return 2;
    case "info":
      return 1;
    case "other":
      return -1;
  }
}

/* ---- Risk tier (posture findings) ----------------------------------------------------------- */

/** Map a risk tier onto a status level for the icon+text `StatusBadge` (design 12.4). */
export function riskTierStatus(tier: RiskTier): StatusLevel {
  switch (tier) {
    case "critical":
    case "high":
      return "error";
    case "medium":
      return "warn";
    case "low":
      return "neutral";
    case "other":
      return "unknown";
  }
}

/** Human label for a risk tier. */
export function riskTierLabel(tier: RiskTier): string {
  switch (tier) {
    case "critical":
      return "Critical";
    case "high":
      return "High";
    case "medium":
      return "Medium";
    case "low":
      return "Low";
    case "other":
      return "Other";
  }
}

/** Risk-tier rank (higher is more severe); `other` ranks lowest. */
export function riskTierRank(tier: RiskTier): number {
  switch (tier) {
    case "critical":
      return 4;
    case "high":
      return 3;
    case "medium":
      return 2;
    case "low":
      return 1;
    case "other":
      return -1;
  }
}

/* ---- The finding's severity/tier badge (either-or, null-safe) ------------------------------- */

/**
 * The one severity/tier badge for a mapped finding. A mapped finding carries EITHER a `severity`
 * (assurance findings) OR a `risk_tier` (posture findings); the contract makes both optional and
 * nullable. We prefer `severity` when present, fall back to `risk_tier`, and show an honest
 * grey "Unrated" `unknown` badge when a finding carries neither rather than inventing a tier
 * (design 12.3). Returns the badge's level, visible label, and a stable sort rank.
 */
export interface SeverityBadge {
  readonly level: StatusLevel;
  readonly label: string;
  readonly title: string;
  /** Sort rank: higher is more severe; the "unrated" fallback ranks below everything real. */
  readonly rank: number;
}

export function severityBadge(finding: MappedFinding): SeverityBadge {
  if (finding.severity != null) {
    return {
      level: severityStatus(finding.severity),
      label: severityLabel(finding.severity),
      title: `Severity: ${severityLabel(finding.severity).toLowerCase()}.`,
      rank: severityRank(finding.severity) + 10, // keep severities ahead of tiers in a mixed list
    };
  }
  if (finding.risk_tier != null) {
    return {
      level: riskTierStatus(finding.risk_tier),
      label: riskTierLabel(finding.risk_tier),
      title: `Risk tier: ${riskTierLabel(finding.risk_tier).toLowerCase()}.`,
      rank: riskTierRank(finding.risk_tier),
    };
  }
  return {
    level: "unknown",
    label: "Unrated",
    title: "This finding carries neither a severity nor a risk tier.",
    rank: -Infinity,
  };
}

/* ---- Crosswalk confidence ------------------------------------------------------------------- */

/**
 * Map the crosswalk `confidence` onto a status level for icon+text display. This is the strength of
 * the MAPPING, not of the finding: a `direct` mapping is the firmest (`neutral` — a confident but
 * non-alarming pill), `related` is `warn` (a looser association the reader should weigh), and
 * `informational` is grey `unknown` (the weakest, context-only link). None is green: a mapping is
 * advisory and never a reassuring "all good".
 */
export function confidenceStatus(confidence: Confidence): StatusLevel {
  switch (confidence) {
    case "direct":
      return "neutral";
    case "related":
      return "warn";
    case "informational":
      return "unknown";
  }
}

/** Human label for a crosswalk confidence. */
export function confidenceLabel(confidence: Confidence): string {
  switch (confidence) {
    case "direct":
      return "Direct";
    case "related":
      return "Related";
    case "informational":
      return "Informational";
  }
}

/** Confidence rank (higher is firmer) so the drill-down can order its findings by mapping strength. */
export function confidenceRank(confidence: Confidence): number {
  switch (confidence) {
    case "direct":
      return 3;
    case "related":
      return 2;
    case "informational":
      return 1;
  }
}

/* ---- Oathera signal ------------------------------------------------------------------------- */

/** Human label for the KIND of Oathera signal that crosswalked a finding to a reference. */
export function signalKindLabel(kind: SignalKind): string {
  switch (kind) {
    case "finding_code":
      return "Finding code";
    case "posture_kind":
      return "Posture kind";
    case "assurance_engine":
      return "Assurance engine";
    case "cwe":
      return "CWE";
  }
}

/** A compact, accessible phrase for a finding's signal (kind + value), e.g. "Finding code: overprivileged". */
export function signalLabel(signal: MappedFinding["signal"]): string {
  return `${signalKindLabel(signal.kind)}: ${signal.value}`;
}

/* ---- Coverage derivation -------------------------------------------------------------------- */

/**
 * A reference partition for the per-reference table and the coverage summary. "Covered" references
 * carry at least one mapped finding; "uncovered" references carry none. The framework always
 * declares its FULL reference set in the response, so an uncovered reference is a real, explicit
 * "nothing mapped here" (distinct from "no data"), surfaced as part of the coverage summary.
 */
export interface ReferencePartition {
  readonly covered: readonly ComplianceReference[];
  readonly uncovered: readonly ComplianceReference[];
}

/** True when a reference has at least one mapped finding. */
export function isReferenceCovered(reference: ComplianceReference): boolean {
  return reference.finding_count > 0;
}

/**
 * Partition the response's references into covered (≥1 mapped finding) and uncovered (none),
 * preserving the server's order within each group. Covered references are what the table drills
 * into; the counts feed the coverage summary alongside the server's own `coverage` totals.
 */
export function partitionReferences(
  mapping: ComplianceMappingResponse,
): ReferencePartition {
  const covered: ComplianceReference[] = [];
  const uncovered: ComplianceReference[] = [];
  for (const reference of mapping.references) {
    if (isReferenceCovered(reference)) covered.push(reference);
    else uncovered.push(reference);
  }
  return { covered, uncovered };
}

/**
 * Order a reference's underlying findings for the drill-down: firmest crosswalk confidence first
 * (direct > related > informational), then most-severe finding first, then most recent. This keeps
 * the strongest, most serious mappings at the top so the reader weighs them first.
 */
export function sortMappedFindings(
  findings: readonly MappedFinding[],
): readonly MappedFinding[] {
  return [...findings].sort((a, b) => {
    const c = confidenceRank(b.confidence) - confidenceRank(a.confidence);
    if (c !== 0) return c;
    const s = severityBadge(b).rank - severityBadge(a).rank;
    if (s !== 0) return s;
    return (b.timestamp ?? 0) - (a.timestamp ?? 0);
  });
}
